<template>
  <div class="wizard-container">

    <!-- ── Left sidebar ─────────────────────────────── -->
    <aside class="wizard-sidebar">
      <div class="sidebar-brand">
        <div class="brand-icon"><Database :size="18" /></div>
        <span class="brand-label">New Connection</span>
      </div>

      <nav class="step-list" aria-label="Setup steps">
        <div
          v-for="(step, i) in STEPS"
          :key="step.id"
          :class="['step-item', {
            active: currentWizardStep === step.id,
            completed: currentWizardStep > step.id && !(step.id === 2 && authWasSkipped),
            skipped: step.id === 2 && authWasSkipped,
          }]"
        >
          <div class="step-track">
            <div class="step-circle">
              <Minus v-if="step.id === 2 && authWasSkipped" :size="14" />
              <CheckCircle2 v-else-if="currentWizardStep > step.id" :size="16" />
              <span v-else>{{ step.id }}</span>
            </div>
            <div v-if="i < STEPS.length - 1" class="step-connector" />
          </div>
          <div class="step-meta">
            <span class="step-name">{{ step.name }}</span>
            <span class="step-hint">{{ step.hint }}</span>
          </div>
        </div>
      </nav>

      <button class="cancel-btn" @click="$emit('cancel')">
        <ArrowLeft :size="15" />
        Back to Connections
      </button>
    </aside>

    <!-- ── Main content ──────────────────────────────── -->
    <main class="wizard-main">

      <!-- Step 1 · Connect ─────────────────────────── -->
      <div v-if="currentWizardStep === 1" class="step-panel">
        <div class="step-header">
          <h2>Connect to API</h2>
          <p class="step-desc">Name your connection and enter the base URL — we'll discover the available endpoints.</p>
        </div>

        <div class="step-body">
          <div class="form-group">
            <label>Connection Name <span class="required">*</span></label>
            <input
              v-model="connectionFormData.connectionDisplayName"
              :placeholder="namePlaceholder"
              class="form-input"
              autofocus
            />
          </div>

          <div class="form-group">
            <label>API Base URL <span class="required">*</span></label>
            <div class="input-with-icon">
              <Globe :size="17" class="input-icon" />
              <input
                v-model="connectionFormData.apiBaseUrl"
                placeholder="https://api.example.com"
                @keyup.enter="executeConnectionValidation"
                :disabled="isProcessingRequest"
                class="form-input"
              />
            </div>
          </div>

          <!-- Discovery summary card -->
          <Transition name="fade">
            <div v-if="discoveryResult" class="discovery-card">
              <div class="discovery-row">
                <CheckCircle2 :size="14" class="disc-ok" />
                <span class="disc-label">API</span>
                <span class="disc-value">{{ discoveryResult.apiTitle || '—' }}</span>
              </div>
              <div class="discovery-row">
                <CheckCircle2 v-if="discoveryResult.openApiSpecUrl" :size="14" class="disc-ok" />
                <Minus v-else :size="14" class="disc-none" />
                <span class="disc-label">OpenAPI spec</span>
                <span class="disc-value">
                  <template v-if="discoveryResult.openApiSpecUrl">Found at <code class="disc-code">{{ discoveryResult.openApiSpecUrl }}</code></template>
                  <template v-else>Not found</template>
                </span>
              </div>
              <div class="discovery-row">
                <CheckCircle2 :size="14" class="disc-ok" />
                <span class="disc-label">Endpoints</span>
                <span class="disc-value">
                  {{ discoveryResult.endpointCount }} discovered<template v-if="discoveryResult.manualEndpointCount"> · {{ discoveryResult.manualEndpointCount }} manual</template>
                </span>
              </div>
              <div class="discovery-row">
                <CheckCircle2 :size="14" class="disc-ok" />
                <span class="disc-label">Auth</span>
                <span class="disc-value">{{ discoveryResult.authSummary }}</span>
              </div>
            </div>
          </Transition>

          <!-- Manual endpoint entry (collapsible) -->
          <CollapsibleRoot v-model:open="manualSectionExpanded" class="manual-section">
            <CollapsibleTrigger class="manual-trigger">
              <div class="trigger-label">
                <Settings :size="15" />
                <span>Add endpoints manually (optional)</span>
              </div>
              <ChevronDown :size="15" :class="['chevron', { open: manualSectionExpanded }]" />
            </CollapsibleTrigger>

            <CollapsibleContent class="manual-content">
              <p class="helper-text">
                If the API has no Swagger/OpenAPI docs, add GET endpoints here.
              </p>

              <div v-if="manualEndpoints.length > 0" class="manual-list">
                <div v-for="(ep, idx) in manualEndpoints" :key="idx" class="manual-item">
                  <div class="manual-item-info">
                    <span class="method-badge">GET</span>
                    <span class="mono">{{ ep.path }}</span>
                  </div>
                  <div class="manual-item-actions">
                    <button @click="editManualEndpoint(idx)" class="icon-btn" title="Edit"><Edit2 :size="13" /></button>
                    <button @click="removeManualEndpoint(idx)" class="icon-btn danger" title="Remove"><Trash2 :size="13" /></button>
                  </div>
                </div>
              </div>

              <div class="add-ep-form">
                <div class="form-group">
                  <label>Endpoint Path</label>
                  <input v-model="newManualEndpoint.path" placeholder="/api/data" class="form-input" />
                </div>

                <div v-if="newManualEndpoint.parameters.length > 0">
                  <label class="sub-label">Query Parameters</label>
                  <div v-for="(param, pi) in newManualEndpoint.parameters" :key="pi" class="param-row">
                    <input v-model="param.name" placeholder="Name" class="form-input" />
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="param.isRequired" />
                      Required
                    </label>
                    <button @click="removeParameter(pi)" class="icon-btn danger"><X :size="13" /></button>
                  </div>
                </div>

                <div class="add-ep-actions">
                  <button @click="addParameter" class="btn-ghost-sm">
                    <Plus :size="13" /> Add parameter
                  </button>
                  <button @click="addManualEndpoint" :disabled="!newManualEndpoint.path" class="btn-outline-sm">
                    {{ editingEndpointIndex !== null ? 'Update' : 'Add endpoint' }}
                  </button>
                  <button v-if="editingEndpointIndex !== null" @click="cancelEdit" class="btn-ghost-sm">
                    Cancel
                  </button>
                </div>
              </div>
            </CollapsibleContent>
          </CollapsibleRoot>
        </div>

        <div class="step-footer">
          <span />
          <button
            @click="handleStep1Continue"
            :disabled="!connectionFormData.apiBaseUrl || isProcessingRequest"
            class="btn-primary"
          >
            <Loader2 v-if="isProcessingRequest" :size="17" class="spin" />
            <ArrowRight v-else-if="discoveryResult" :size="17" />
            <Zap v-else :size="17" />
            {{ isProcessingRequest ? 'Testing…' : discoveryResult ? 'Looks good — Continue' : 'Test & Continue' }}
          </button>
        </div>
      </div>

      <!-- Step 2 · Authenticate ───────────────────── -->
      <div v-if="currentWizardStep === 2" class="step-panel">
        <div class="step-header">
          <h2>Configure Authentication</h2>
          <p class="step-desc">Choose how to authenticate with this API.</p>
        </div>

        <div class="step-body">
          <!-- Visual auth-type selector cards -->
          <div class="auth-cards">
            <button
              v-for="opt in visibleAuthOptions"
              :key="opt.value"
              :class="['auth-card', { selected: connectionFormData.authenticationConfiguration.authenticationType === opt.value, detected: isDetectedAuthType(opt.value) }]"
              @click="connectionFormData.authenticationConfiguration.authenticationType = opt.value"
            >
              <div class="auth-card-icon">
                <component :is="opt.icon" :size="20" />
              </div>
              <div class="auth-card-text">
                <span class="auth-card-title">{{ opt.label }}</span>
                <span class="auth-card-hint">{{ opt.description }}</span>
                <span v-if="isDetectedAuthType(opt.value)" class="detected-badge">Auto-detected ✓</span>
              </div>
              <CheckCircle2
                v-if="connectionFormData.authenticationConfiguration.authenticationType === opt.value"
                :size="16"
                class="auth-card-check"
              />
            </button>
          </div>

          <button
            v-if="detectedAuthTypes.length > 0"
            class="show-all-toggle"
            @click="showAllAuthOptions = !showAllAuthOptions"
          >
            {{ showAllAuthOptions ? 'Show fewer options' : 'Show all options' }}
          </button>

          <!-- Credential fields (shown when not "None") -->
          <Transition name="fade">
            <div
              v-if="connectionFormData.authenticationConfiguration.authenticationType !== 'None'"
              class="credentials-block"
            >
              <!-- API Key -->
              <template v-if="connectionFormData.authenticationConfiguration.authenticationType === 'ApiKeyHeader'">
                <div class="form-group">
                  <label>API Key Value <span class="required">*</span></label>
                  <input
                    v-model="connectionFormData.authenticationConfiguration.tokenValue"
                    type="password"
                    placeholder="Enter your API key"
                    class="form-input"
                  />
                </div>
                <div class="form-group">
                  <label>Header Name</label>
                  <input
                    v-model="connectionFormData.authenticationConfiguration.keyFieldName"
                    placeholder="X-API-Key"
                    class="form-input"
                  />
                </div>
              </template>

              <!-- Bearer Token -->
              <template v-if="connectionFormData.authenticationConfiguration.authenticationType === 'BearerToken'">
                <div class="form-group">
                  <label>Bearer Token <span class="required">*</span></label>
                  <input
                    v-model="connectionFormData.authenticationConfiguration.tokenValue"
                    type="password"
                    placeholder="Enter your bearer token"
                    class="form-input"
                  />
                </div>

                <!-- OAuth2 contextual guidance -->
                <div class="oauth2-guidance">
                  <div class="oauth2-guidance-header">
                    <AlertCircle :size="14" />
                    <span>{{ detectedOAuth2Info ? 'OAuth 2.0 detected' : 'Bearer Token' }}</span>
                  </div>

                  <template v-if="detectedOAuth2Info && (detectedOAuth2Info.authorizationUrl || detectedOAuth2Info.tokenUrl || detectedOAuth2Info.scopes?.length)">
                    <div class="oauth2-urls">
                      <div v-if="detectedOAuth2Info.authorizationUrl" class="oauth2-url-row">
                        <span class="oauth2-url-label">Authorization URL</span>
                        <a :href="detectedOAuth2Info.authorizationUrl" target="_blank" rel="noopener noreferrer" class="oauth2-url-link">
                          {{ detectedOAuth2Info.authorizationUrl }}
                          <ExternalLink :size="11" />
                        </a>
                      </div>
                      <div v-if="detectedOAuth2Info.tokenUrl" class="oauth2-url-row">
                        <span class="oauth2-url-label">Token URL</span>
                        <a :href="detectedOAuth2Info.tokenUrl" target="_blank" rel="noopener noreferrer" class="oauth2-url-link">
                          {{ detectedOAuth2Info.tokenUrl }}
                          <ExternalLink :size="11" />
                        </a>
                      </div>
                      <div v-if="detectedOAuth2Info.scopes?.length" class="oauth2-url-row">
                        <span class="oauth2-url-label">Scopes</span>
                        <span class="oauth2-scopes">{{ detectedOAuth2Info.scopes.join(', ') }}</span>
                      </div>
                    </div>
                  </template>

                  <ol class="oauth2-steps">
                    <li>Register this application with your identity provider</li>
                    <li>Complete the OAuth2 flow to obtain an access token</li>
                    <li>Paste the token in the field above</li>
                  </ol>
                </div>
              </template>

              <!-- Basic Auth -->
              <template v-if="connectionFormData.authenticationConfiguration.authenticationType === 'BasicAuth'">
                <div class="credentials-row">
                  <div class="form-group">
                    <label>Username <span class="required">*</span></label>
                    <input
                      v-model="connectionFormData.authenticationConfiguration.usernameCredential"
                      placeholder="Username"
                      class="form-input"
                    />
                  </div>
                  <div class="form-group">
                    <label>Password <span class="required">*</span></label>
                    <input
                      v-model="connectionFormData.authenticationConfiguration.passwordCredential"
                      type="password"
                      placeholder="Password"
                      class="form-input"
                    />
                  </div>
                </div>
              </template>
            </div>
          </Transition>
        </div>

        <div class="step-footer">
          <button @click="currentWizardStep = 1" class="btn-back">
            <ArrowLeft :size="16" /> Back
          </button>

          <!-- Change E: None skips the auth test entirely -->
          <button
            v-if="connectionFormData.authenticationConfiguration.authenticationType === 'None'"
            @click="currentWizardStep = 3"
            class="btn-primary"
          >
            Continue <ArrowRight :size="17" />
          </button>
          <button
            v-else
            @click="executeAuthenticationValidation"
            :disabled="isProcessingRequest"
            class="btn-primary"
          >
            <Loader2 v-if="isProcessingRequest" :size="17" class="spin" />
            <Shield v-else :size="17" />
            {{ isProcessingRequest ? 'Testing…' : 'Test & Continue' }}
          </button>
        </div>
      </div>

      <!-- Step 3 · Endpoints ───────────────────────── -->
      <div v-if="currentWizardStep === 3" class="step-panel step-panel-wide">
        <div class="step-header">
          <h2>Select Endpoints</h2>
          <p class="step-desc">
            <template v-if="selectedEndpointIndices.length">
              <span class="selection-pill">{{ selectedEndpointIndices.length }} selected</span>
              — deselect any you don't need
            </template>
            <template v-else>Pick which endpoints to expose in this connection.</template>
          </p>
        </div>

        <div v-if="authWasSkipped" class="public-api-banner">
          <AlertCircle :size="15" />
          This API is public — no authentication required.
        </div>

        <!-- Change F: two-column endpoint layout -->
        <div class="step-body endpoints-layout">
          <!-- Left: checklist -->
          <div class="endpoints-checklist">
            <div v-if="discoveredApiEndpoints.length > 0" class="select-all-row">
              <button class="select-all-btn" @click="toggleSelectAll">
                {{ allEndpointsSelected ? 'Deselect all' : 'Select all' }}
              </button>
            </div>

            <div v-if="discoveredApiEndpoints.length === 0" class="empty-endpoints">
              <Database :size="36" />
              <p>No endpoints discovered</p>
            </div>

            <div
              v-for="(endpoint, index) in discoveredApiEndpoints"
              :key="index"
              :class="['endpoint-row', {
                selected: selectedEndpointIndices.includes(index),
                focused: focusedEndpointIndex === index,
              }]"
              @click="toggleAndFocusEndpoint(index, endpoint)"
            >
              <input
                type="checkbox"
                :checked="selectedEndpointIndices.includes(index)"
                @click.stop
                @change="toggleAndFocusEndpoint(index, endpoint)"
                class="endpoint-checkbox"
              />
              <span class="method-badge">{{ endpoint.method }}</span>
              <span class="mono endpoint-path-text">{{ endpoint.path }}</span>
              <span v-if="endpoint.parameters?.length" class="params-pill">
                {{ endpoint.parameters.length }}p
              </span>
            </div>
          </div>

          <!-- Right: parameter config panel -->
          <div class="param-panel">
            <div v-if="focusedEndpointIndex === null" class="param-panel-empty">
              <MousePointerClick :size="34" />
              <p>Select an endpoint to configure its parameters</p>
            </div>

            <div v-else-if="!discoveredApiEndpoints[focusedEndpointIndex]?.parameters?.length" class="param-panel-empty">
              <CheckCircle2 :size="34" />
              <p>No parameters needed for this endpoint</p>
            </div>

            <template v-else>
              <div class="param-panel-header">
                <span class="method-badge">{{ discoveredApiEndpoints[focusedEndpointIndex].method }}</span>
                <span class="mono param-panel-path">{{ discoveredApiEndpoints[focusedEndpointIndex].path }}</span>
              </div>
              <div class="param-fields">
                <div
                  v-for="param in discoveredApiEndpoints[focusedEndpointIndex].parameters"
                  :key="param.name"
                  class="param-field"
                >
                  <label>
                    {{ param.name }}
                    <span v-if="param.isRequired" class="required">*</span>
                    <span v-if="param.description" class="param-desc"> – {{ param.description }}</span>
                  </label>
                  <select
                    v-if="param.enumValues?.length"
                    :value="getParameterValue(focusedEndpointIndex, param.name)"
                    @input="setParameterValue(focusedEndpointIndex, param.name, $event.target.value)"
                    class="form-input"
                  >
                    <option value="">Select {{ param.name }}</option>
                    <option v-for="v in param.enumValues" :key="v" :value="v">{{ v }}</option>
                  </select>
                  <input
                    v-else
                    :value="getParameterValue(focusedEndpointIndex, param.name)"
                    @input="setParameterValue(focusedEndpointIndex, param.name, $event.target.value)"
                    :placeholder="param.defaultValue || `Enter ${param.name}`"
                    :required="param.isRequired"
                    :type="param.type === 'integer' ? 'number' : 'text'"
                    class="form-input"
                  />
                </div>
              </div>
            </template>
          </div>
        </div>

        <div v-if="!canSaveConnection && selectedEndpointIndices.length > 0" class="validation-warning">
          <AlertCircle :size="15" />
          Fill in all required parameters (marked *) for selected endpoints.
        </div>

        <div class="step-footer">
          <button @click="authWasSkipped ? currentWizardStep = 1 : currentWizardStep = 2" class="btn-back">
            <ArrowLeft :size="16" /> Back
          </button>
          <button
            @click="persistConnectionConfiguration"
            :disabled="!canSaveConnection || isProcessingRequest"
            class="btn-primary"
          >
            <Loader2 v-if="isProcessingRequest" :size="17" class="spin" />
            <Save v-else :size="17" />
            {{ isProcessingRequest ? 'Saving…' : 'Save Connection' }}
          </button>
        </div>
      </div>

    </main>
  </div>
</template>

<script setup>
// @ts-nocheck
import { ref, computed } from 'vue'
import {
  CollapsibleRoot, CollapsibleTrigger, CollapsibleContent,
} from 'radix-vue'
import {
  Database, Globe, Settings, ChevronDown, Edit2, Trash2, X, Plus,
  Zap, Loader2, CheckCircle2, ArrowRight, ArrowLeft, Shield, Save,
  AlertCircle, ExternalLink, Link, Key, Lock, User, MousePointerClick, Minus,
} from 'lucide-vue-next'
import apiService from '@/Modules/ETL/services/api'
import { useNotification } from '@/Modules/ETL/composables/useNotification'

const { success, error } = useNotification()
const emit = defineEmits(['connection-configuration-saved', 'cancel'])

// ── Static config ────────────────────────────────────────
const STEPS = [
  { id: 1, name: 'Connect',      hint: 'Name it and enter the API URL' },
  { id: 2, name: 'Authenticate', hint: 'Set up credentials'            },
  { id: 3, name: 'Endpoints',    hint: 'Select what data to expose'    },
]

const AUTH_OPTIONS = [
  { value: 'None',         label: 'No Authentication', description: 'Public API — no credentials needed',           icon: Globe  },
  { value: 'ApiKeyHeader', label: 'API Key',            description: 'Pass a key in a request header',              icon: Key    },
  { value: 'BearerToken',  label: 'Bearer Token',       description: 'OAuth / JWT in Authorization header',         icon: Lock   },
  { value: 'BasicAuth',    label: 'Basic Auth',         description: 'Username and password credentials',           icon: User   },
]

// ── State ────────────────────────────────────────────────
const currentWizardStep    = ref(1)
const isProcessingRequest  = ref(false)
const discoveryResult      = ref(null)
const authWasSkipped       = ref(false)
const manualSectionExpanded = ref(false)
const manualEndpoints      = ref([])
const editingEndpointIndex = ref(null)
const newManualEndpoint    = ref({ method: 'GET', path: '', parameters: [] })

const connectionFormData = ref({
  apiBaseUrl: '',
  connectionDisplayName: '',
  authenticationConfiguration: {
    authenticationType: 'None',
    tokenValue: '',
    keyFieldName: 'X-API-Key',
    usernameCredential: '',
    passwordCredential: '',
  },
})

const discoveredApiEndpoints  = ref([])
const connectionValidationResult = ref(null)
const selectedEndpointIndices = ref([])
const endpointParameterValues = ref({})
const focusedEndpointIndex    = ref(null)
const showAllAuthOptions      = ref(false)

// ── Computed ─────────────────────────────────────────────
const namePlaceholder = computed(() =>
  isProcessingRequest.value
    ? 'Auto-filled from API — you can rename this'
    : 'e.g. Salesforce Production'
)

const canSaveConnection = computed(() => {
  if (!connectionFormData.value.connectionDisplayName) return false
  if (selectedEndpointIndices.value.length === 0) return false

  return selectedEndpointIndices.value.every((index) => {
    const endpoint = discoveredApiEndpoints.value[index]
    if (!endpoint.parameters?.length) return true
    const paramValues = endpointParameterValues.value[index] || {}
    return endpoint.parameters.every(
      (param) => !param.isRequired || (paramValues[param.name] && paramValues[param.name] !== '')
    )
  })
})

const detectedAuthTypes = computed(() =>
  connectionValidationResult.value?.authInfo?.supportedAuthMethods?.map(m => m.authType) || []
)

const visibleAuthOptions = computed(() => {
  if (showAllAuthOptions.value || detectedAuthTypes.value.length === 0) return AUTH_OPTIONS
  return AUTH_OPTIONS.filter(opt => opt.value === 'None' || detectedAuthTypes.value.includes(opt.value))
})

const isDetectedAuthType = (value) => detectedAuthTypes.value.includes(value)

const allEndpointsSelected = computed(() =>
  discoveredApiEndpoints.value.length > 0 &&
  selectedEndpointIndices.value.length === discoveredApiEndpoints.value.length
)

const detectedOAuth2Info = computed(() => {
  const methods = connectionValidationResult.value?.authInfo?.supportedAuthMethods || []
  return methods.find(m => m.authType === 'OAuth2' || m.authType === 'BearerToken') || null
})

// ── Helpers ──────────────────────────────────────────────
const AUTH_TYPE_LABELS = {
  ApiKeyHeader: 'API Key',
  BearerToken: 'Bearer Token',
  BasicAuth: 'Basic Auth',
  OAuth2: 'OAuth 2.0',
}

const buildAuthSummary = (authInfo) => {
  if (!authInfo?.isRequired) return 'None (public API)'
  const recommended = authInfo.supportedAuthMethods?.find(m => m.isRecommended)
  if (!recommended) return 'Required'
  const label = AUTH_TYPE_LABELS[recommended.authType] || recommended.authType
  return `${label} (auto-detected)`
}

// ── Methods ──────────────────────────────────────────────
const handleStep1Continue = () => {
  if (!discoveryResult.value) {
    executeConnectionValidation()
    return
  }
  const authInfo = connectionValidationResult.value?.authInfo
  if (!authInfo || !authInfo.isRequired) {
    connectionFormData.value.authenticationConfiguration.authenticationType = 'None'
    authWasSkipped.value = true
    currentWizardStep.value = 3
  } else {
    authWasSkipped.value = false
    showAllAuthOptions.value = false
    const recommended = connectionValidationResult.value?.authInfo?.supportedAuthMethods?.find(m => m.isRecommended)
    if (recommended) {
      connectionFormData.value.authenticationConfiguration.authenticationType = recommended.authType
    }
    currentWizardStep.value = 2
  }
}

const executeConnectionValidation = async () => {
  isProcessingRequest.value = true
  connectionValidationResult.value = null
  discoveryResult.value = null

  try {
    const manualEndpointsPayload = manualEndpoints.value.length > 0
      ? manualEndpoints.value.map((ep) => ({
          method: ep.method,
          path: ep.path,
          parameters: ep.parameters.map((p) => ({
            name: p.name, location: 'query', isRequired: p.isRequired,
            type: 'string', enumValues: null, defaultValue: null, description: null,
          })),
        }))
      : null

    const response = await apiService.validateApiConnection(
      connectionFormData.value.apiBaseUrl, manualEndpointsPayload, null
    )

    connectionValidationResult.value = response.data
    discoveredApiEndpoints.value = response.data.endpoints || []
    selectedEndpointIndices.value = discoveredApiEndpoints.value.map((_, i) => i)
    discoveredApiEndpoints.value.forEach((ep, i) => initializeParameterValues(i, ep))

    if (response.data.apiTitle && !connectionFormData.value.connectionDisplayName) {
      connectionFormData.value.connectionDisplayName = response.data.apiTitle
    }

    if (response.data.isConnected && response.data.endpoints?.length > 0) {
      discoveryResult.value = {
        apiTitle: response.data.apiTitle || null,
        openApiSpecUrl: response.data.openApiSpecUrl || null,
        endpointCount: (response.data.endpoints || []).length,
        manualEndpointCount: manualEndpoints.value.length,
        authSummary: buildAuthSummary(response.data.authInfo),
      }
    } else {
      error('Connection Failed', response.data.errorMessage || 'No endpoints found')
    }
  } catch (err) {
    error('Connection Failed', err.errorMessage || 'Failed to connect to API')
  }

  isProcessingRequest.value = false
}

const executeAuthenticationValidation = async () => {
  isProcessingRequest.value = true

  try {
    await apiService.validateAuthentication(
      connectionFormData.value.apiBaseUrl,
      connectionFormData.value.authenticationConfiguration
    )
    success('Authentication Valid', 'Credentials verified successfully')
    currentWizardStep.value = 3
  } catch (err) {
    error('Authentication Failed', err.errorMessage || 'Invalid credentials')
  }

  isProcessingRequest.value = false
}

const persistConnectionConfiguration = async () => {
  isProcessingRequest.value = true

  try {
    const selectedEndpointConfigs = selectedEndpointIndices.value.map((index) => {
      const endpoint = discoveredApiEndpoints.value[index]
      const paramValues = endpointParameterValues.value[index] || {}
      return {
        method: endpoint.method,
        path: endpoint.path,
        parameters: endpoint.parameters?.map((p) => ({
          name: p.name,
          value: paramValues[p.name] || p.defaultValue || null,
          isRequired: p.isRequired,
        })) || [],
      }
    })

    const payload = {
      name: connectionFormData.value.connectionDisplayName,
      baseUrl: connectionValidationResult.value?.baseUrl || connectionFormData.value.apiBaseUrl,
      basePath: connectionValidationResult.value?.basePath || '',
      selectedEndpoints: selectedEndpointConfigs,
      authConfig: {
        type: connectionFormData.value.authenticationConfiguration.authenticationType,
        token: connectionFormData.value.authenticationConfiguration.tokenValue || null,
        keyName: connectionFormData.value.authenticationConfiguration.keyFieldName || null,
        username: connectionFormData.value.authenticationConfiguration.usernameCredential || null,
        password: connectionFormData.value.authenticationConfiguration.passwordCredential || null,
      },
    }

    const response = await apiService.persistConnection(payload)
    success('Connection Saved', `"${response.data.name}" saved successfully`)
    emit('connection-configuration-saved', response.data)
  } catch (err) {
    error('Save Failed', err.errorMessage || 'Failed to save connection')
  }

  isProcessingRequest.value = false
}

// Endpoint selection + focus (step 3)
const toggleSelectAll = () => {
  if (allEndpointsSelected.value) {
    selectedEndpointIndices.value = []
  } else {
    selectedEndpointIndices.value = discoveredApiEndpoints.value.map((_, i) => i)
    discoveredApiEndpoints.value.forEach((ep, i) => initializeParameterValues(i, ep))
  }
}

const toggleAndFocusEndpoint = (index, endpoint) => {
  const pos = selectedEndpointIndices.value.indexOf(index)
  if (pos === -1) {
    selectedEndpointIndices.value.push(index)
    initializeParameterValues(index, endpoint)
  } else {
    selectedEndpointIndices.value.splice(pos, 1)
  }
  focusedEndpointIndex.value = index
}

const initializeParameterValues = (index, endpoint) => {
  if (!endpointParameterValues.value[index]) {
    endpointParameterValues.value[index] = {}
  }
  endpoint.parameters?.forEach((param) => {
    if (endpointParameterValues.value[index][param.name] === undefined) {
      endpointParameterValues.value[index][param.name] = param.defaultValue || ''
    }
  })
}

const getParameterValue = (endpointIndex, paramName) =>
  endpointParameterValues.value[endpointIndex]?.[paramName] || ''

const setParameterValue = (endpointIndex, paramName, value) => {
  if (!endpointParameterValues.value[endpointIndex]) {
    endpointParameterValues.value[endpointIndex] = {}
  }
  endpointParameterValues.value[endpointIndex][paramName] = value
}

// Manual endpoint helpers
const addParameter = () => {
  newManualEndpoint.value.parameters.push({ name: '', isRequired: false })
}
const removeParameter = (index) => newManualEndpoint.value.parameters.splice(index, 1)

const addManualEndpoint = () => {
  if (!newManualEndpoint.value.path) return
  let path = newManualEndpoint.value.path
  if (!path.startsWith('/')) path = '/' + path
  const endpoint = {
    method: 'GET',
    path,
    parameters: newManualEndpoint.value.parameters.filter((p) => p.name.trim() !== ''),
  }
  if (editingEndpointIndex.value !== null) {
    manualEndpoints.value[editingEndpointIndex.value] = endpoint
    editingEndpointIndex.value = null
  } else {
    manualEndpoints.value.push(endpoint)
  }
  newManualEndpoint.value = { method: 'GET', path: '', parameters: [] }
}

const editManualEndpoint = (index) => {
  const ep = manualEndpoints.value[index]
  newManualEndpoint.value = { method: ep.method, path: ep.path, parameters: JSON.parse(JSON.stringify(ep.parameters)) }
  editingEndpointIndex.value = index
}

const cancelEdit = () => {
  newManualEndpoint.value = { method: 'GET', path: '', parameters: [] }
  editingEndpointIndex.value = null
}

const removeManualEndpoint = (index) => {
  manualEndpoints.value.splice(index, 1)
  if (editingEndpointIndex.value === index) cancelEdit()
}
</script>

<style scoped>
/* ── Layout ─────────────────────────────────────────────── */
.wizard-container {
  display: flex;
  height: calc(100vh - 78px);
  overflow: hidden;
  background: #f5f6f8;
}

/* ── Sidebar ─────────────────────────────────────────────── */
.wizard-sidebar {
  width: 252px;
  flex-shrink: 0;
  background: white;
  border-right: 1px solid rgba(0, 0, 0, 0.07);
  display: flex;
  flex-direction: column;
  padding: 1.75rem 1.5rem;
}

.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-bottom: 2.5rem;
}

.brand-icon {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.brand-label {
  font-size: 15px;
  font-weight: 700;
  color: var(--color-text-primary);
}

/* Step list */
.step-list {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.step-item {
  display: flex;
  gap: 0.875rem;
  opacity: 0.38;
  transition: opacity 0.25s;
}

.step-item.active,
.step-item.completed {
  opacity: 1;
}

.step-track {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}

.step-circle {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.07);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
  color: var(--color-text-secondary);
  transition: background 0.3s, color 0.3s;
  flex-shrink: 0;
}

.step-item.active .step-circle {
  background: var(--color-primary);
  color: white;
  box-shadow: 0 2px 10px rgba(123, 24, 28, 0.3);
}

.step-item.completed .step-circle {
  background: var(--color-success);
  color: white;
}

.step-item.skipped {
  opacity: 0.5;
}
.step-item.skipped .step-circle {
  background: var(--color-border, #e5e7eb);
  color: var(--color-text-muted, #9ca3af);
}

.step-connector {
  width: 2px;
  flex: 1;
  min-height: 28px;
  background: rgba(0, 0, 0, 0.07);
  border-radius: 2px;
  margin: 4px 0;
}

.step-meta {
  padding-bottom: 2rem;
  padding-top: 0.3rem;
}

.step-name {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-primary);
  line-height: 1.3;
}

.step-hint {
  display: block;
  font-size: 11.5px;
  color: var(--color-text-secondary);
  margin-top: 0.2rem;
  line-height: 1.4;
}

.cancel-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: var(--color-text-secondary);
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  padding: 0.5rem 0.25rem;
  border-radius: 6px;
  transition: color 0.2s;
  text-align: left;
}

.cancel-btn:hover {
  color: var(--color-text-primary);
}

/* ── Main content ────────────────────────────────────────── */
.wizard-main {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.step-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  max-width: 680px;
  width: 100%;
  margin: 0 auto;
  padding: 2.5rem 2rem 2rem;
}

/* Step 3 gets more horizontal space for the two-col layout */
.step-panel-wide {
  max-width: 960px;
}

.step-header {
  margin-bottom: 2rem;
}

.step-header h2 {
  margin: 0 0 0.4rem;
  font-size: 22px;
  font-weight: 700;
  color: var(--color-text-primary);
}

.step-desc {
  margin: 0;
  font-size: 14px;
  color: var(--color-text-secondary);
  line-height: 1.5;
}

.step-body {
  flex: 1;
}

.step-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1.75rem;
  margin-top: 1.75rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

/* ── Forms ───────────────────────────────────────────────── */
.form-group {
  margin-bottom: 1.25rem;
}

.form-group label,
.sub-label {
  display: block;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 0.45rem;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.sub-label {
  margin-top: 0.75rem;
}

.form-input {
  width: 100%;
  padding: 0.75rem 0.875rem;
  border: 1.5px solid rgba(0, 0, 0, 0.1);
  border-radius: 9px;
  font-size: 14px;
  background: white;
  transition: border-color 0.2s, box-shadow 0.2s;
  font-family: inherit;
  color: var(--color-text-primary);
}

.form-input:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.12);
}

.form-input:disabled {
  background: rgba(0, 0, 0, 0.03);
  cursor: not-allowed;
}

.input-with-icon {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 0.875rem;
  color: var(--color-text-muted);
  pointer-events: none;
}

.input-with-icon .form-input {
  padding-left: 2.75rem;
}

.required {
  color: var(--color-error);
}

/* ── Buttons ─────────────────────────────────────────────── */
.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: 9px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.2s, box-shadow 0.2s;
  font-family: inherit;
  box-shadow: 0 2px 8px rgba(123, 24, 28, 0.2);
}

.btn-primary:hover:not(:disabled) {
  opacity: 0.92;
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(123, 24, 28, 0.28);
}

.btn-primary:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none;
}

.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.625rem 1rem;
  background: transparent;
  color: var(--color-text-secondary);
  border: 1.5px solid rgba(0, 0, 0, 0.1);
  border-radius: 9px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.btn-back:hover {
  border-color: var(--color-accent);
  color: var(--color-accent);
}

.btn-ghost-sm {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.375rem 0.625rem;
  background: transparent;
  border: none;
  color: var(--color-text-secondary);
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.2s, color 0.2s;
}

.btn-ghost-sm:hover {
  background: rgba(0, 0, 0, 0.04);
  color: var(--color-text-primary);
}

.btn-outline-sm {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.375rem 0.875rem;
  background: white;
  border: 1.5px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--color-text-primary);
}

.btn-outline-sm:hover:not(:disabled) {
  border-color: var(--color-accent);
  color: var(--color-accent);
}

.btn-outline-sm:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: white;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.icon-btn:hover { border-color: var(--color-accent); color: var(--color-accent); }
.icon-btn.danger:hover { border-color: var(--color-error); color: var(--color-error); background: rgba(220,38,38,0.04); }

/* ── Public API banner ───────────────────────────────────── */
.public-api-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.875rem;
  background: rgba(59, 130, 246, 0.07);
  border: 1.5px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
  font-size: 13.5px;
  color: #2563eb;
  margin-bottom: 1.25rem;
}

/* ── Discovery summary card ──────────────────────────────── */
.discovery-card {
  padding: 0.875rem 1rem;
  background: rgba(22, 163, 74, 0.06);
  border: 1.5px solid rgba(22, 163, 74, 0.22);
  border-radius: 10px;
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}
.discovery-row {
  display: grid;
  grid-template-columns: 16px 80px 1fr;
  align-items: center;
  gap: 0.5rem;
  font-size: 13px;
}
.disc-ok  { color: var(--color-success); flex-shrink: 0; }
.disc-none { color: var(--color-text-muted); flex-shrink: 0; }
.disc-label {
  font-weight: 600;
  color: var(--color-text-secondary);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.disc-value { color: var(--color-text-primary); }
.disc-code {
  font-family: monospace;
  font-size: 12px;
  background: rgba(0,0,0,0.05);
  padding: 1px 4px;
  border-radius: 4px;
}

/* ── Manual endpoints ────────────────────────────────────── */
.manual-section {
  margin: 1.25rem 0;
  border: 1.5px dashed rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  overflow: hidden;
}

.manual-trigger {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.875rem 1rem;
  background: rgba(0, 0, 0, 0.02);
  border: none;
  cursor: pointer;
  transition: background 0.2s;
  font-family: inherit;
}

.manual-trigger:hover { background: rgba(0, 0, 0, 0.04); }

.trigger-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.chevron { transition: transform 0.25s; color: var(--color-text-muted); }
.chevron.open { transform: rotate(180deg); }

.manual-content { padding: 1rem; }
.helper-text { font-size: 12.5px; color: var(--color-text-secondary); margin-bottom: 0.875rem; }

.manual-list { display: flex; flex-direction: column; gap: 0.375rem; margin-bottom: 0.875rem; }

.manual-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.625rem 0.875rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 7px;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.manual-item-info { display: flex; align-items: center; gap: 0.625rem; }
.manual-item-actions { display: flex; gap: 0.375rem; }

.add-ep-form {
  padding: 1rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  margin-top: 0.75rem;
}

.add-ep-actions { display: flex; gap: 0.5rem; align-items: center; margin-top: 0.75rem; }

.param-row { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem; }
.param-row .form-input { flex: 1; }

.checkbox-label { display: flex; align-items: center; gap: 0.375rem; font-size: 12.5px; white-space: nowrap; cursor: pointer; color: var(--color-text-secondary); }
.checkbox-label input[type="checkbox"] { width: 15px; height: 15px; cursor: pointer; }

/* ── Auth cards ──────────────────────────────────────────── */
.auth-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.auth-card {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.875rem 1rem;
  border: 1.5px solid rgba(0, 0, 0, 0.09);
  border-radius: 11px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  font-family: inherit;
}

.auth-card:hover {
  border-color: rgba(102, 126, 234, 0.4);
  background: rgba(102, 126, 234, 0.02);
}

.auth-card.selected {
  border-color: var(--color-primary);
  background: var(--color-primary-bg);
}

.auth-card-icon {
  width: 38px;
  height: 38px;
  border-radius: 9px;
  background: rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  flex-shrink: 0;
  transition: all 0.2s;
}

.auth-card.selected .auth-card-icon {
  background: var(--color-primary);
  color: white;
}

.auth-card-text { flex: 1; min-width: 0; }

.auth-card-title {
  display: block;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--color-text-primary);
  line-height: 1.3;
}

.auth-card-hint {
  display: block;
  font-size: 11.5px;
  color: var(--color-text-secondary);
  margin-top: 0.15rem;
  line-height: 1.4;
}

.auth-card-check { color: var(--color-primary); flex-shrink: 0; }

.detected-badge {
  display: inline-block;
  margin-top: 0.25rem;
  font-size: 10.5px;
  font-weight: 600;
  color: var(--color-success);
  background: rgba(22, 163, 74, 0.1);
  border: 1px solid rgba(22, 163, 74, 0.25);
  border-radius: 99px;
  padding: 0.1rem 0.45rem;
  letter-spacing: 0.02em;
}

.show-all-toggle {
  background: none;
  border: none;
  font-family: inherit;
  font-size: 12.5px;
  color: var(--color-accent);
  cursor: pointer;
  padding: 0.25rem 0;
  margin-bottom: 1rem;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.show-all-toggle:hover {
  opacity: 0.8;
}

.credentials-block { animation: fadeSlide 0.25s ease; }
.credentials-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }

/* ── OAuth2 guidance ─────────────────────────────────────── */
.oauth2-guidance {
  margin-top: 0.25rem;
  padding: 0.875rem 1rem;
  background: rgba(59, 130, 246, 0.05);
  border: 1.5px solid rgba(59, 130, 246, 0.18);
  border-radius: 10px;
  font-size: 13px;
}

.oauth2-guidance-header {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-weight: 700;
  color: #2563eb;
  margin-bottom: 0.75rem;
  font-size: 12.5px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.oauth2-urls {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  margin-bottom: 0.875rem;
}

.oauth2-url-row {
  display: grid;
  grid-template-columns: 110px 1fr;
  align-items: start;
  gap: 0.5rem;
  font-size: 12.5px;
}

.oauth2-url-label {
  font-weight: 600;
  color: var(--color-text-secondary);
  white-space: nowrap;
  padding-top: 1px;
}

.oauth2-url-link {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: #2563eb;
  text-decoration: none;
  word-break: break-all;
  font-family: 'Monaco', 'Consolas', monospace;
  font-size: 11.5px;
}

.oauth2-url-link:hover { text-decoration: underline; }

.oauth2-scopes {
  font-family: 'Monaco', 'Consolas', monospace;
  font-size: 11.5px;
  color: var(--color-text-primary);
  word-break: break-all;
}

.oauth2-steps {
  margin: 0;
  padding-left: 1.25rem;
  color: var(--color-text-secondary);
  font-size: 12.5px;
  line-height: 1.7;
}

/* ── Step 3: endpoint layout ─────────────────────────────── */
.endpoints-layout {
  display: flex;
  gap: 1.25rem;
  min-height: 380px;
  height: 100%;
}

.endpoints-checklist {
  flex: 0 0 52%;
  border: 1.5px solid rgba(0, 0, 0, 0.08);
  border-radius: 11px;
  overflow-y: auto;
  background: white;
}

.empty-endpoints {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: var(--color-text-muted);
  gap: 0.625rem;
}

.empty-endpoints svg { opacity: 0.3; }
.empty-endpoints p { font-size: 13.5px; }

.endpoint-row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: background 0.15s;
  user-select: none;
}

.endpoint-row:last-child { border-bottom: none; }
.endpoint-row:hover { background: rgba(0, 0, 0, 0.02); }
.endpoint-row.selected { background: rgba(102, 126, 234, 0.05); }
.endpoint-row.focused {
  background: rgba(102, 126, 234, 0.09);
  box-shadow: inset 3px 0 0 var(--color-accent);
}

.select-all-row {
  padding: 0.5rem 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(0, 0, 0, 0.015);
}

.select-all-btn {
  background: none;
  border: none;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-accent);
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.select-all-btn:hover { opacity: 0.75; }

.endpoint-checkbox { width: 15px; height: 15px; cursor: pointer; flex-shrink: 0; }
.endpoint-path-text { font-size: 13px; color: var(--color-text-primary); flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.params-pill {
  font-size: 10.5px;
  font-weight: 600;
  color: var(--color-text-secondary);
  background: rgba(0, 0, 0, 0.05);
  padding: 0.15rem 0.45rem;
  border-radius: 99px;
  white-space: nowrap;
  flex-shrink: 0;
}

/* Right panel */
.param-panel {
  flex: 1;
  border: 1.5px solid rgba(0, 0, 0, 0.08);
  border-radius: 11px;
  background: white;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.param-panel-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted);
  gap: 0.625rem;
  text-align: center;
  padding: 2rem;
}

.param-panel-empty svg { opacity: 0.25; }
.param-panel-empty p { font-size: 13px; line-height: 1.5; }

.param-panel-header {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(0, 0, 0, 0.02);
  flex-shrink: 0;
}

.param-panel-path { font-size: 12.5px; color: var(--color-text-primary); }

.param-fields { padding: 1rem; display: flex; flex-direction: column; gap: 1rem; }

.param-field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-secondary);
  margin-bottom: 0.35rem;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.param-desc { font-weight: 400; color: var(--color-text-muted); text-transform: none; letter-spacing: 0; }

/* ── Shared ──────────────────────────────────────────────── */
.method-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  background: var(--color-success);
  color: white;
  border-radius: 5px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

.mono { font-family: 'Monaco', 'Consolas', monospace; }

.selection-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.6rem;
  background: var(--color-accent-light);
  color: var(--color-accent);
  border-radius: 99px;
  font-size: 12px;
  font-weight: 600;
  margin-left: 0.5rem;
}

.validation-warning {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--color-warning-bg);
  border: 1.5px solid var(--color-warning-light);
  border-radius: 8px;
  color: var(--color-warning);
  font-size: 13px;
  font-weight: 500;
  margin-top: 1rem;
}

/* ── Animations ──────────────────────────────────────────── */
.spin { animation: spin 0.8s linear infinite; }

@keyframes spin { to { transform: rotate(360deg); } }

@keyframes fadeSlide {
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
}

.fade-enter-active, .fade-leave-active { transition: all 0.25s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; transform: translateY(-4px); }
</style>
